import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';

/** One of the people named to mark a squad's athletes at one competition. */
export interface CompetitionTeamManager {
  user_id: string;
  full_name: string;
  username?: string | null;
  /**
   * What the member app's profile page is addressed by. Null for somebody who
   * has not accepted their invitation — they have no public profile yet.
   */
  slug?: string | null;
  email: string;
  avatar_small_url?: string | null;
  /** Invited by us and not signed up yet — "waiting on them", not a stranger. */
  pending: boolean;
}

/**
 * A squad read through one competition: the circuit's list on a stage, with the
 * managers named for **this** weekend and the number of entrants here.
 *
 * `managers` is absent for a viewer who may not name them — the entry form asks
 * the same endpoint for a list of names, and who accompanies a squad is the
 * admins' business.
 */
export interface CompetitionTeam {
  id: string;
  name: string;
  /** How many of this competition's entrants are in this squad. */
  entries_count?: number;
  managers?: CompetitionTeamManager[];
}

class CompetitionTeamManagersService {
  /**
   * The squads an entry at this competition can be put down as — the circuit's
   * on a stage — each with the team managers named *here*.
   */
  async listForCompetition(competitionSlug: string): Promise<CompetitionTeam[]> {
    const response = await apiClient.get(`/api/competitions/${competitionSlug}/teams`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the teams');
    }

    const data: { data: Array<{ attributes: CompetitionTeam }> } = await response.json();
    return data.data.map((item) => item.attributes);
  }

  /**
   * Names a team manager of one squad **at one competition** by email. An address
   * we already know is appointed; one we do not gets an account opened for it and
   * an invitation to claim it. Answers with the whole squad, so one card redraws
   * from one response.
   *
   * Always the competition's slug, never the circuit's: the appointment is for a
   * weekend (see the backend's CompetitionTeamManager), and a stage names its own.
   */
  async addManager(
    competitionSlug: string,
    teamId: string,
    manager: { email: string; name?: string; surname?: string }
  ): Promise<CompetitionTeam> {
    const response = await apiClient.post(
      `/api/competitions/${competitionSlug}/teams/${teamId}/managers`,
      { data: { attributes: manager } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to name the team manager');
    }

    const data: { data: { attributes: CompetitionTeam } } = await response.json();
    return data.data.attributes;
  }

  /** Drops one appointment. Their appointments at other stages are untouched. */
  async removeManager(
    competitionSlug: string,
    teamId: string,
    userId: string
  ): Promise<CompetitionTeam> {
    const response = await apiClient.delete(
      `/api/competitions/${competitionSlug}/teams/${teamId}/managers/${userId}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to remove the team manager');
    }

    const data: { data: { attributes: CompetitionTeam } } = await response.json();
    return data.data.attributes;
  }
}

export const competitionTeamManagersService = new CompetitionTeamManagersService();
